import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, KeyRound } from "lucide-react";

export function BulkPinDialog({ open, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) { setResults(null); setUsers([]); setSel([]); return; }
    setLoading(true);
    api.get("/users/pin/legacy").then((r) => {
      const u = r.data.users || [];
      setUsers(u); setSel(u.map((x) => x.id));
    }).catch((e) => toast.error(apiError(e.response?.data?.detail) || "Liste alınamadı"))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const run = async () => {
    if (sel.length === 0) return toast.error("En az bir kullanıcı seçin");
    setBusy(true);
    try {
      const { data } = await api.post("/users/pin/bulk-reset", { user_ids: sel });
      setResults(data.results || []);
      toast.success(`${data.count} kullanıcıya yeni PIN atandı`);
      onDone?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Atama başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><KeyRound className="h-5 w-5 text-sky-600" /> Toplu PIN Ata</DialogTitle>
          <DialogDescription className="sr-only">Eski PIN'li kullanıcılara toplu yeni PIN atama</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="py-8 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></p>
        ) : results ? (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Yeni PIN'ler atandı. Kullanıcılara iletebilirsiniz — istediğiniz zaman Kullanıcılar sayfasından tekrar görüntülenebilir.</p>
            <div className="max-h-72 overflow-y-auto rounded-md ring-1 ring-inset ring-slate-200">
              <table className="w-full text-sm">
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id} data-testid={`bulk-pin-result-${r.id}`} className="border-b border-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-700">{r.name}</td>
                      <td className="px-3 py-2 text-right font-mono text-lg font-bold tracking-widest text-sky-700">{r.pin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-2">
              <Button data-testid="bulk-pin-close" onClick={onClose} className="bg-slate-800 text-white hover:bg-slate-700">Kapat</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {users.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Eski / görüntülenemeyen PIN'li kullanıcı yok — tüm PIN'ler zaten görüntülenebilir. 🎉</p>
            ) : (
              <>
                <p className="text-sm text-slate-600">PIN'i <b>eski (yalnız şifreli özet, görüntülenemez)</b> olan kullanıcılar. Seçilenlere tek seferde yeni rastgele PIN atanır (rol uzunluğuna uygun) ve görüntülenebilir olur.</p>
                <div className="max-h-72 overflow-y-auto rounded-md ring-1 ring-inset ring-slate-200">
                  {users.map((u) => (
                    <label key={u.id} data-testid={`bulk-pin-user-${u.id}`} className="flex cursor-pointer items-center gap-3 border-b border-slate-50 px-3 py-2 hover:bg-slate-50">
                      <input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)} className="h-4 w-4 accent-sky-600" />
                      <span className="flex-1 text-sm font-medium text-slate-700">{u.name}</span>
                      <span className="text-xs text-slate-400">{u.role_label}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-slate-400">{sel.length} / {users.length} seçili</span>
                  <Button data-testid="bulk-pin-run" onClick={run} disabled={busy || sel.length === 0} className="bg-sky-600 text-white hover:bg-sky-700">
                    {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Atanıyor…</> : <><RefreshCw className="mr-1.5 h-4 w-4" /> Yeni PIN Ata</>}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
